// AppShell · campus v2 (FUNDACIÓN)
// ─────────────────────────────────────────────────────────────────────────
// Nuevo shell con SIDEBAR lateral. Sustituye al header horizontal como
// navegación principal del campus, conservando las utilidades (ThemeToggle +
// NotificationBell + acceso al perfil). Las páginas existentes se renderizan
// DENTRO de este shell sin cambios (las zonas se rehacen en pasos posteriores).
//
// Características de la fundación:
//  · Sidebar EXPANDIDO por defecto · estado plegado/expandido persistido en
//    localStorage. Plegado = icon-rail (~72px) con TOOLTIP por icono al hover.
//  · Item activo teñido con el color de firma (--sig) de su sección.
//  · El AppShell fija --sig según la sección de la ruta actual (data-section).
//  · Móvil: el sidebar se oculta y se abre como overlay con scrim vía hamburguesa.
//  · Contenedor de contenido UNIFICADO (mismo ancho/gutters para todas las páginas).
//
// Iconos: lucide-react (set fijado). Estilos: clases .appshell* en index.css.

import { Fragment, startTransition, useCallback, useEffect, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronLeft, LogOut, Menu, Search } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { useTheme } from '../lib/theme-context';
import {
  NAV_GROUPS,
  SECTION_LABEL,
  isSubItemActive,
  isNavItemVisible,
  visibleSubItems,
  resolveNavItem,
  type NavAccessContext,
  type NavItem,
  type SectionSlug,
} from '../lib/appshell-nav';
import ThemeToggle from './ThemeToggle';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import { preloadSidebarRoutes } from '../lib/route-preload';

const COLLAPSE_KEY = 'robotea-shell-collapsed';

/** Iniciales para el avatar del pie (a partir del nombre o el email). */
function initialsFrom(name: string | null, email: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  }
  const local = email.split('@')[0] ?? '';
  return (local.slice(0, 2) || '?').toUpperCase();
}

export default function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const { state, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  // Navegación del sidebar en TRANSICIÓN (anti-parpadeo). Las páginas son chunks
  // lazy; sin esto, al pulsar un item React suspende y pinta el fallback (antes
  // a pantalla completa, ahora limitado al contenido por el Suspense interno).
  // Con startTransition, React mantiene el contenido ACTUAL visible mientras
  // carga el chunk de la nueva sección y solo conmuta cuando está listo → no se
  // ve spinner ni hueco. Respetamos modificadores (cmd/ctrl/click central) y
  // enlaces externos para no romper "abrir en pestaña nueva".
  const navTransition = useCallback(
    (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return; // deja el comportamiento nativo (nueva pestaña, etc.)
      }
      e.preventDefault();
      startTransition(() => {
        navigate(to);
      });
    },
    [navigate],
  );

  // Logo REAL theme-aware (assets de PR #606 en public/). Wordmark cuando el
  // rail está expandido; isotipo cuando está plegado. En oscuro usamos la
  // variante violeta-azul/blanco; en claro la variante navy/negra que contrasta
  // sobre superficie clara.
  const wordmarkSrc = isLight ? '/robotea-logo-dark.png' : '/robotea-logo.png';
  const iconSrc = isLight ? '/robotea-icon-black.png' : '/robotea-icon-white.png';

  // ── Estado plegado · persistido ──────────────────────────────────────
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* storage no disponible · solo runtime */
    }
  }, [collapsed]);

  // ── Búsqueda global (modal compartido GlobalSearch) ──────────────────
  const [searchOpen, setSearchOpen] = useState(false);
  // Tecla de atajo según plataforma · solo cosmético en la pista del campo.
  const shortcutKey =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
      ? '⌘K'
      : 'Ctrl K';

  // ── Overlay móvil ────────────────────────────────────────────────────
  const [mobileOpen, setMobileOpen] = useState(false);
  // Cierra el overlay al navegar (si no, queda abierto encima del contenido).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  // Escape cierra el overlay.
  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setMobileOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const authenticated = state.status === 'authenticated';
  const userName = authenticated ? state.identity.name : null;
  const userEmail = authenticated ? state.identity.email : '';
  // Permisos del rol efectivo (vienen del backend). Gobiernan qué se muestra en
  // el sidebar (F3 RBAC) en lugar del antiguo gate binario role==='admin'.
  const permissions = authenticated ? state.identity.permissions : [];
  // Contexto de acceso del nav · membresías del usuario efectivo (bajo
  // impersonación = el alumno objetivo). Hoy: membresía RoboTeam.
  const navAccess: NavAccessContext = {
    roboteamMember: authenticated ? state.identity.roboteam?.member === true : false,
  };
  // ¿Es personal con acceso al panel? = tiene cualquier permiso de admin.
  // Reemplaza a `role === 'admin'` para precarga de rutas y etiqueta del pie.
  const isStaff = permissions.some((p) => p.startsWith('admin.'));

  const activeItem = resolveNavItem(location.pathname);
  const activeSection: SectionSlug = activeItem?.section ?? 'inicio';

  // ── Submenús anidados (hoy solo Panel admin) ─────────────────────────
  // El usuario puede plegar/desplegar el submenú manualmente; cuando navega a
  // una ruta de ese item lo abrimos automáticamente (para que su sección activa
  // siempre quede visible). `manualOverride` recuerda si el usuario lo cerró a
  // mano estando en una de sus rutas, para no re-abrirlo en cada render.
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  // Subgrupos PLEGABLES dentro de un submenú (Jaime 2026-06-17): al abrir el
  // "Panel admin" los grupos salen PLEGADOS (solo cabeceras); el usuario expande
  // el que quiera. Guardamos las claves `${item.to}::${grupo}` EXPANDIDAS
  // (defecto = vacío = todo plegado). El grupo con la ruta activa se abre solo.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const onActiveSubmenuRoute = activeItem?.children ? activeItem.to : null;
  useEffect(() => {
    // Al entrar en una ruta con submenú, lo abrimos. Al salir, no forzamos nada
    // (respetamos el estado que tuviera).
    if (onActiveSubmenuRoute) setOpenSubmenu(onActiveSubmenuRoute);
  }, [onActiveSubmenuRoute]);

  // El submenú desplegado (p.ej. "Panel admin") permanece abierto al hacer clic
  // en el contenido de la página; solo se cierra desde su propia cabecera.
  // Revierte el cierre-al-clicar-fuera de 2026-06-16 (Jaime 2026-07-04).

  // Precarga ociosa de los chunks de las secciones del sidebar al montar el
  // shell, para que el primer clic en cada item sea instantáneo (sin esperar a
  // descargar el chunk). Idempotente. Solo con sesión autenticada.
  useEffect(() => {
    if (authenticated) preloadSidebarRoutes(isStaff);
  }, [authenticated, isStaff]);

  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);
  const handleLogout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      navigate('/login');
    }
  }, [signOut, navigate]);

  const renderItem = (item: NavItem): JSX.Element | null => {
    if (!isNavItemVisible(item, permissions, navAccess)) return null;
    const Icon = item.icon;

    // Sub-items VISIBLES para este usuario (por permiso). Una cabecera de
    // submenú solo se llega a renderizar si tiene al menos uno (isNavItemVisible).
    const subItems = item.children ? visibleSubItems(item, permissions) : [];

    // ── Item con submenú anidado (cabecera expandible) ────────────────
    if (subItems.length > 0) {
      const sectionActive = activeItem?.to === item.to;
      const expanded = openSubmenu === item.to;
      const sigStyle = { '--sig': `var(--sig-${item.section})` } as React.CSSProperties;
      const subId = `submenu-${item.section}`;
      return (
        <div key={item.to} className="appshell-group" style={sigStyle}>
          <button
            type="button"
            className={`appshell-item appshell-item--parent${sectionActive ? ' is-active' : ''}`}
            onClick={() => setOpenSubmenu((cur) => (cur === item.to ? null : item.to))}
            aria-expanded={expanded}
            aria-controls={subId}
            data-testid={item.testId}
          >
            <Icon className="appshell-ico" size={20} strokeWidth={2} aria-hidden="true" />
            <span className="appshell-label">{item.label}</span>
            <ChevronDown
              className={`appshell-caret${expanded ? ' is-open' : ''}`}
              size={16}
              strokeWidth={2.25}
              aria-hidden="true"
            />
            {/* Flyout en estado PLEGADO · navega sin expandir el rail. */}
            <span className="appshell-flyout" role="menu" aria-label={item.label}>
              <span className="appshell-flyout-title">{item.label}</span>
              {subItems.map((sub) => {
                const subActive = isSubItemActive(location.pathname, sub);
                return (
                  <Link
                    key={sub.to}
                    to={sub.to}
                    role="menuitem"
                    className={`appshell-flyout-item${subActive ? ' is-active' : ''}`}
                    aria-current={subActive ? 'page' : undefined}
                    data-testid={`${sub.testId}-fly`}
                    onClick={navTransition(sub.to)}
                  >
                    {sub.label}
                  </Link>
                );
              })}
            </span>
          </button>

          <div
            id={subId}
            className={`appshell-submenu${expanded ? ' is-open' : ''}`}
            role="group"
            aria-label={item.label}
            aria-hidden={!expanded}
            // `inert` saca los sub-enlaces del árbol de accesibilidad y del
            // foco cuando el submenú está cerrado (coherente con aria-expanded
            // del padre), sin renunciar a la animación grid-rows.
            {...(!expanded ? { inert: '' } : {})}
          >
            {/* wrapper único · el grid-template-rows 0fr→1fr anima sobre esta
                fila sin reflow por píxel (technique grid-rows, no height) */}
            <div className="appshell-submenu-inner">
              {(() => {
                // Render del sub-item (link con punto + label).
                const renderSub = (sub: typeof subItems[number]) => {
                  const subActive = isSubItemActive(location.pathname, sub);
                  return (
                    <Link
                      key={sub.to}
                      to={sub.to}
                      className={`appshell-subitem${subActive ? ' is-active' : ''}`}
                      aria-current={subActive ? 'page' : undefined}
                      tabIndex={expanded ? 0 : -1}
                      data-testid={sub.testId}
                      onClick={navTransition(sub.to)}
                    >
                      <span className="appshell-subdot" aria-hidden="true" />
                      <span className="appshell-sublabel">{sub.label}</span>
                    </Link>
                  );
                };
                // Items sin grupo (p. ej. "Resumen") sueltos arriba; el resto se
                // agrupa bajo cabeceras PLEGABLES (Jaime 2026-06-17) por orden de
                // primera aparición.
                const ungrouped = subItems.filter((s) => !s.group);
                const groupsOrder: string[] = [];
                for (const s of subItems) {
                  if (s.group && !groupsOrder.includes(s.group)) groupsOrder.push(s.group);
                }
                return (
                  <>
                    {ungrouped.map(renderSub)}
                    {groupsOrder.map((g) => {
                      const gkey = `${item.to}::${g}`;
                      const groupItems = subItems.filter((s) => s.group === g);
                      // Defecto PLEGADO: solo abierto si el usuario lo expandió o
                      // si contiene la ruta activa (no esconder dónde estás).
                      const hasActive = groupItems.some((s) => isSubItemActive(location.pathname, s));
                      const open = expandedGroups.has(gkey) || hasActive;
                      return (
                        <Fragment key={gkey}>
                          <button
                            type="button"
                            className={`appshell-subgroup${open ? '' : ' is-collapsed'}`}
                            onClick={() => toggleGroup(gkey)}
                            aria-expanded={open}
                            tabIndex={expanded ? 0 : -1}
                          >
                            <ChevronDown className="appshell-subgroup-caret" size={13} strokeWidth={2.5} aria-hidden="true" />
                            <span>{g}</span>
                          </button>
                          {open && groupItems.map(renderSub)}
                        </Fragment>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      );
    }

    // ── Item simple (link directo) ────────────────────────────────────
    const isActive = activeItem?.to === item.to;
    return (
      <Link
        key={item.to}
        to={item.to}
        className={`appshell-item${isActive ? ' is-active' : ''}`}
        style={{ '--sig': `var(--sig-${item.section})` } as React.CSSProperties}
        aria-current={isActive ? 'page' : undefined}
        data-testid={item.testId}
        onClick={navTransition(item.to)}
      >
        <Icon className="appshell-ico" size={20} strokeWidth={2} aria-hidden="true" />
        <span className="appshell-label">{item.label}</span>
        <span className="appshell-tip" role="tooltip">{item.label}</span>
      </Link>
    );
  };

  return (
    <div
      className={`appshell${collapsed ? ' is-collapsed' : ''}${mobileOpen ? ' is-mobile-open' : ''}`}
      data-section={activeSection}
      style={{ '--sig': `var(--sig-${activeSection})` } as React.CSSProperties}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[70]
                   focus:rounded-lg focus:bg-brand-violet focus:px-4 focus:py-2
                   focus:text-sm focus:font-medium focus:text-white"
      >
        Saltar al contenido
      </a>

      {/* ───────────────────────── SIDEBAR ───────────────────────── */}
      <aside className="appshell-rail" aria-label="Navegación principal">
        {/* Cabecera del rail · logo a la izquierda + botón Plegar a su derecha
            (Jaime 2026-06-18). Antes el botón vivía en el pie; ahora vive arriba
            junto a la marca. En estado plegado el botón sigue accesible para
            expandir. */}
        <div className="appshell-railhead">
          <Link
            to={authenticated ? '/dashboard' : '/'}
            className="appshell-brand"
            aria-label="Ir al inicio · Robotea Campus"
          >
            {/* Isotipo (rail plegado) + wordmark (rail expandido), ambos
                theme-aware. El CSS muestra uno u otro según el estado del rail. */}
            <img className="appshell-brand-icon" src={iconSrc} alt="" aria-hidden="true" />
            <img className="appshell-brand-logo" src={wordmarkSrc} alt="Robotea" />
          </Link>
          <button
            type="button"
            className="appshell-collapse appshell-collapse--head"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expandir menú' : 'Plegar menú'}
            aria-pressed={collapsed}
            data-testid="shell-collapse"
          >
            <ChevronLeft className="chev" size={18} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <nav className="appshell-nav" aria-label="Secciones del campus">
          {NAV_GROUPS.map((group, gi) => {
            const visible = group.filter((it) => isNavItemVisible(it, permissions, navAccess));
            if (visible.length === 0) return null;
            return (
              <div key={gi} style={{ display: 'contents' }}>
                {gi > 0 && <div className="appshell-sep" aria-hidden="true" />}
                {group.map(renderItem)}
              </div>
            );
          })}
        </nav>

        <div className="appshell-foot">
          <Link to="/perfil" className="appshell-user" aria-label="Mi perfil">
            <span className="appshell-avatar" aria-hidden="true">
              {authenticated ? initialsFrom(userName, userEmail) : '··'}
            </span>
            <span className="appshell-user-meta">
              <span className="nm">{userName ?? (userEmail || 'Mi cuenta')}</span>
              <span className="pl">{isStaff ? 'Administración' : 'Mi perfil'}</span>
            </span>
          </Link>
          <button
            type="button"
            className="appshell-collapse appshell-logout"
            onClick={handleLogout}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
            data-testid="shell-logout"
          >
            <LogOut className="chev" size={18} strokeWidth={2} aria-hidden="true" />
            <span className="ctxt">Salir</span>
          </button>
        </div>
      </aside>

      {/* scrim del overlay móvil */}
      <button
        type="button"
        className="appshell-scrim"
        aria-label="Cerrar menú"
        tabIndex={mobileOpen ? 0 : -1}
        onClick={() => setMobileOpen(false)}
      />

      {/* ────────────────────────── MAIN ────────────────────────── */}
      <div className="appshell-main">
        <header className="appshell-top">
          <button
            type="button"
            className="appshell-hamburger"
            aria-label="Abrir menú de navegación"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
            data-testid="shell-hamburger"
          >
            <Menu size={20} strokeWidth={2} aria-hidden="true" />
          </button>

          {/* Logo en la barra superior · SOLO móvil (el rail con el logo está
              oculto como drawer ≤768px). En escritorio el logo vive en el rail. */}
          <img className="appshell-top-logo" src={wordmarkSrc} alt="Robotea" />

          {/* Volver a la página anterior (petición Jaime 2026-08-06). Con
              historial dentro del SPA hace back real; si se entró directo por
              URL (idx 0 · sin atrás) lleva al Inicio para no salir del campus.
              Oculto en ≤640px (revisión #1294: rompía el presupuesto de ancho
              de la topbar en 360-390px; en móvil ya hay gesto/botón atrás). */}
          {authenticated && (
            <button
              type="button"
              className="appshell-back"
              aria-label="Volver a la página anterior"
              title="Volver atrás"
              onClick={() => {
                const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
                if (idx > 0) navigate(-1);
                else navigate('/dashboard', { replace: true });
              }}
              data-testid="shell-back"
            >
              <ChevronLeft size={18} strokeWidth={2} aria-hidden="true" />
            </button>
          )}

          <nav className="appshell-crumb" aria-label="Ruta">
            <span>Campus</span>
            <span className="sep" aria-hidden="true">/</span>
            <b>{SECTION_LABEL[activeSection]}</b>
          </nav>

          <div className="appshell-spacer" />

          {authenticated && (
            <>
              {/* Caja de búsqueda · abre el modal GlobalSearch (módulos +
                  comentarios, búsqueda real en backend). En ≥sm es un campo;
                  en móvil colapsa al icono de la derecha. */}
              <button
                type="button"
                className="appshell-search"
                onClick={() => setSearchOpen(true)}
                aria-label="Buscar en el campus"
                data-testid="shell-search"
              >
                <Search className="appshell-search-ico" size={17} strokeWidth={2} aria-hidden="true" />
                <span className="appshell-search-text">Buscar módulos, lecciones…</span>
                <kbd className="appshell-search-kbd" aria-hidden="true">{shortcutKey}</kbd>
              </button>
              <button
                type="button"
                className="appshell-search-mini"
                onClick={() => setSearchOpen(true)}
                aria-label="Buscar en el campus"
                data-testid="shell-search-mini"
              >
                <Search size={18} strokeWidth={2} aria-hidden="true" />
              </button>
            </>
          )}

          <ThemeToggle />
          {authenticated && <NotificationBell />}
        </header>

        <main id="main-content" tabIndex={-1} className="appshell-content">
          {children}
        </main>
      </div>

      {/* Modal de búsqueda · controlado por la caja de la barra superior y por
          el atajo Cmd/Ctrl+K (registrado dentro del propio GlobalSearch).
          `headless` es OBLIGATORIO: sin él, en estado cerrado GlobalSearch cae a
          su botón-trigger por defecto y, montado como hijo directo del flex
          `.appshell`, ese botón se estira a toda la altura → aparecía como una
          "columna" fantasma pegada al borde derecho que rompía el layout y se
          comía los clics de esa franja. Con `headless` el cerrado devuelve null
          y el único trigger visible es la caja `.appshell-search` de la barra. */}
      {authenticated && (
        <GlobalSearch headless open={searchOpen} onOpenChange={setSearchOpen} />
      )}
    </div>
  );
}
